import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { CCol, CRow } from '@coreui/react';
import { CXButton, CXDatePicker } from '../../../components/CXForm';
import CXTimePicker from '../../../components/CXForm/CXTimePicker';
import CXFPConfigTextArea from '../../../components/CXForm/CXFPConfigTextArea';
import _ from 'lodash';
import moment from 'moment';
import { INewsModalData, ModalType } from './news';
import { itemColumns, NEWS_REQUIRED_FIELDS } from '../../../constants/newsConstant';
import CXFPConfigTextInput from '../../../components/CXForm/CXFPConfigTextInput';
import { ActionType } from './ITable';

interface AddOrEditNewsModalProps {
  modalType: ModalType,
  modalData: INewsModalData,
  actionsCallback?: (action: ActionType, data: any) => void;
}

const AddOrEditNewsModal: React.FC<AddOrEditNewsModalProps> = ({
  modalType,
  modalData,
  actionsCallback,
}) => {
  const isShowModal = [ModalType.ADD, ModalType.EDIT].includes(modalType);
  const isAddNews = modalType === ModalType.ADD;
  const modalTitle = isAddNews ? 'Add news' : 'Edit news';
  const [data, setData] = useState(_.cloneDeep(modalData));
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);
  const [validDateErrorMsg, setValidDateErrorMsg] = useState<string>('');
  const closeModal = () => {
    // close modal
    actionsCallback?.(ActionType.CLOSE_MODAL, null);
  };

  const onChangeModal = (value: string, field: string) => {
    console.log(`🔧 onChangeModal - field: ${field}, value: ${value}`);
    let tempObj = {
      ...data,
    };
    tempObj[field] = value;
    console.log(`📊 Updated data - validFrom: ${tempObj.validFrom}, validTo: ${tempObj.validTo}`);
    setData(tempObj);
  };
  const handleDateChange = (value: string, field: string, state: any, setState: Function) => {
    console.log(`📅 handleDateChange - field: ${field}, value: ${value}`);
    const newDate = moment.utc(value);

    // if current "valid to" or "from " have no value, use default time: 00:00 to 23:59
    const isValidTo = field.includes('validTo') || field.includes('ValidTo');
    const defaultTime = isValidTo ? moment.utc().hour(23).minute(59).second(0) : moment.utc().startOf('day');
    const currentValue = state[field] ? moment.utc(state[field]) : defaultTime;

    newDate.set({
      hour: currentValue.hour(),
      minute: currentValue.minute(),
      second: currentValue.second()
    });
    const updatedValue = newDate.toISOString();
    console.log(`📅 handleDateChange - updated value: ${updatedValue}`);

    let tempObj = {
      ...data,
    };
    tempObj[field] = updatedValue;
    setData(tempObj);
  };
  const handleTimeChange = (value: string, field: string, state: any, setState: Function) => {
    console.log(`⏰ handleTimeChange - field: ${field}, value: ${value}`);
    const newTime = moment.utc(value);

    // if currentValue no value, use today
    const currentValue = state[field] ? moment.utc(state[field]) : moment.utc().startOf('day');

    currentValue.set({
      hour: newTime.hour(),
      minute: newTime.minute(),
      second: newTime.second()
    });
    const updatedValue = currentValue.toISOString();
    console.log(`⏰ handleTimeChange - updated value: ${updatedValue}`);

    let tempObj = {
      ...data,
    };
    tempObj[field] = updatedValue;    setData(tempObj);
  };

  const isValidDuration = (dateFromStr: string, dateToStr: string) => {
    const dateFrom = new Date(dateFromStr);
    const dateTo = new Date(dateToStr);
    return dateFrom <= dateTo;
  }
  const validDurationCheck = (value: string) => {
    console.log(`⏰ validDurationCheck - input value: ${value}`);
    console.log(`📅 Current data.validFrom: ${data.validFrom}`);
    console.log(`📅 Current data.validTo: ${data.validTo}`);

    let isValid = true;
    if (hasValidationError) {
      // check if required date fields is filled
      if (!requiredValidation(value)) {
        setValidDateErrorMsg('Valid date may not be blank.');
        isValid = false;
      }

      // once filled, then compare validFrom and validTo, validFrom should be earlier than validTo
      if (isValid && data.validFrom && data.validTo) {
        const isValidDur = isValidDuration(data.validFrom, data.validTo);
        console.log(`🔍 Duration validation result: ${isValidDur}`);
        if (!isValidDur) {
          setValidDateErrorMsg('please input valid duration');
          isValid = false;
        }
      }
    }
    console.log(`✅ validDurationCheck final result: ${isValid}`);
    return isValid;
  }
  const modalValidationCheck = () => {
    let isValid = true;
    for (const field in data) {
      const value = data[field];
      if (NEWS_REQUIRED_FIELDS.includes(field) && (!value || value === "" || (value && value.length === 0))) {
        setHasValidationError(true);
        isValid = false;
      }
    }

    if (isValid) {
      // compare validFrom and validTo, validFrom should be earlier than validTo
      if (!isValidDuration(data.validFrom, data.validTo)) {
        setHasValidationError(true);
        isValid = false;
      }
    }
    return isValid;
  }

  const onClickCreateOrUpdate = () => {
    // validation check
    if (!modalValidationCheck()) {
      return;
    }
    setHasValidationError(false);
    // create or update news
    actionsCallback?.(isAddNews ? ActionType.ADD : ActionType.EDIT_SAVE, data);
  };
  const requiredValidation = (value: string | string[]) => {
    if (hasValidationError) {
      if (typeof value === "string" || typeof value === "object") {
        return value && value.length > 0;
      }
    }

    return true;
  }

  return (
    <Modal className="newsModal" show={isShowModal} onHide={closeModal} size="xl">
      <Modal.Header className="modal-header" closeButton>
        <Modal.Title className='modal-title'>
          {modalTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <CRow>
        <CCol xs={6}>
            <span className='modal-section-title'>News information</span>
          </CCol>
          <CCol xs={6}>
          <CRow>
              <CCol xs={6}>
                <CXDatePicker
                  placeholder={"Valid from date"}
                  value={data.validFrom || null}
                  onChange={setData}
                  zIndex={1340}
                  onChangeField={handleDateChange}
                  field={itemColumns.validFrom.dbField}
                  state={data}
                  hasDefault={false}
                  additionalValidation={validDurationCheck}
                  errMsg={validDateErrorMsg}
                />
              </CCol>
              <CCol xs={6}>
                <CXDatePicker
                  placeholder={"Valid to date"}
                  value={data.validTo || null}
                  onChange={setData}
                  zIndex={1340}
                  onChangeField={handleDateChange}
                  field={itemColumns.validTo.dbField}
                  state={data}
                  hasDefault={false}
                  additionalValidation={validDurationCheck}
                  errMsg={validDateErrorMsg}
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol xs={6}>
                <CXTimePicker
                  placeholder={"Valid from time"}
                  value={data.validFrom || null}
                  onChange={setData}
                  onChangeField={handleTimeChange}
                  field={itemColumns.validFrom.dbField}
                  state={data}
                  zIndex={1330}
                />
              </CCol>
              <CCol xs={6}>
                <CXTimePicker
                  placeholder={"Valid to time"}
                  value={data.validTo || null}
                  onChange={setData}
                  onChangeField={handleTimeChange}
                  field={itemColumns.validTo.dbField}
                  state={data}
                  zIndex={1320}
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol xs={12}>
                <CXFPConfigTextInput
                  placeholder={"Title"}
                  value={data.title}
                  onChangeField={(e) => onChangeModal(e.target.value, itemColumns.title.dbField)}
                  additionalValidation={requiredValidation}
                  errMsg={"Title may not be blank."}
                  zIndex={1031}
                />
              </CCol>
            </CRow>
          </CCol>
        </CRow>
        <hr className="solid"></hr>
        <CRow>
          <CCol xs={6}>
            <span className='modal-section-title'>News content</span>
          </CCol>
          <CCol xs={6}>
            <div className={'newsContent'}>
              <CXFPConfigTextArea
                maxRows={21}
                value={data.content}
                placeholder={"News content"}
                onChangeField={(e) => onChangeModal(e.target.value, itemColumns.content.dbField)}
                additionalValidation={requiredValidation}
                errMsg={"News content may not be blank."}
              />
            </div>
          </CCol>
        </CRow>
      </Modal.Body>
      <CRow>
        <CCol>
          <Modal.Footer className="modal-footer">
            <div className='cancel-button'><CXButton outline={true} text={"Cancel"} onClick={() => closeModal()} fontSize={"16px"} width={"100%"} /></div>
            <div className='save-button'><CXButton text={isAddNews ? "Add" : "Update"} onClick={() => onClickCreateOrUpdate()} fontSize={"16px"} width={"100%"} /></div>
          </Modal.Footer>
        </CCol>
      </CRow>
    </Modal>
  );
};

export default AddOrEditNewsModal;